import { frontmatterSchema, type Frontmatter } from "@blog/content-schema";
import { parse as parseYaml, stringify as stringifyYaml } from "yaml";

/**
 * YAML frontmatter (SPEC §12).
 *
 * The canonical published format is Markdown + frontmatter, so this is where a
 * file on disk becomes a validated object and back. It is the only place that
 * reads or writes the `---` block.
 */

/** A document split into its frontmatter block and its body. */
export interface SplitDocument {
  /** The YAML text between the delimiters, without them. `null` when absent. */
  readonly frontmatter: string | null;
  readonly body: string;
}

/**
 * Matches a leading frontmatter block.
 *
 * Anchored to the start of the file: a `---` further down is a thematic break
 * or a setext heading underline, not frontmatter. CRLF is accepted because a
 * content repository may be edited on Windows.
 *
 * The trailing `\r?\n*` swallows the blank line that conventionally separates
 * the block from the body, so `body` starts at the first real line of content
 * rather than at a newline.
 */
const FRONTMATTER_PATTERN = /^---[ \t]*\r?\n([\s\S]*?)\r?\n?---[ \t]*\r?\n*/;

/** Split a document into frontmatter and body without interpreting either. */
export function splitFrontmatter(markdown: string): SplitDocument {
  // A byte-order mark would stop the pattern from anchoring.
  const normalised = markdown.replace(/^\uFEFF/, "");
  const match = FRONTMATTER_PATTERN.exec(normalised);

  if (match === null) {
    return { frontmatter: null, body: normalised };
  }

  return {
    frontmatter: match[1] ?? "",
    body: normalised.slice(match[0].length),
  };
}

/** The frontmatter as parsed YAML, before validation. */
export function readFrontmatter(markdown: string): { raw: unknown; body: string } {
  const { frontmatter, body } = splitFrontmatter(markdown);

  if (frontmatter === null) {
    return { raw: {}, body };
  }

  return { raw: parseYaml(frontmatter) as unknown, body };
}

export interface ParsedDocument {
  readonly data: Frontmatter;
  readonly body: string;
}

/**
 * Parse and validate a document.
 *
 * Throws when the frontmatter is missing or invalid: a file that cannot be
 * validated is one the site cannot render or route, and silently defaulting
 * would publish a broken page. The Zod issue list is attached so the sync job
 * can report *which* field is wrong.
 */
export function parseFrontmatter(markdown: string): ParsedDocument {
  const { raw, body } = readFrontmatter(markdown);
  const result = frontmatterSchema.safeParse(raw);

  if (!result.success) {
    const issues = result.error.issues
      .map((issue) => `${issue.path.map(String).join(".") || "(root)"}: ${issue.message}`)
      .join("; ");

    throw new FrontmatterError(`Invalid frontmatter: ${issues}`, result.error);
  }

  return { data: result.data, body };
}

/** Raised when frontmatter is absent or fails validation. */
export class FrontmatterError extends Error {
  override readonly name = "FrontmatterError";

  constructor(message: string, override readonly cause?: unknown) {
    super(message);
  }
}

/**
 * Serialize frontmatter back to YAML.
 *
 * Key order follows the schema declaration order rather than the object's
 * insertion order, so a re-publish of an unchanged entry produces byte-identical
 * frontmatter and therefore no spurious Git diff.
 */
export function serializeFrontmatter(data: Frontmatter): string {
  const ordered: Record<string, unknown> = {};

  for (const key of FRONTMATTER_KEY_ORDER) {
    const value = (data as Record<string, unknown>)[key];

    if (value !== undefined) {
      ordered[key] = value;
    }
  }

  // Preserve any passthrough keys the author added by hand, after the known
  // ones, so a round-trip through the CMS does not drop them.
  for (const [key, value] of Object.entries(data)) {
    if (!(key in ordered) && value !== undefined) {
      ordered[key] = value;
    }
  }

  return stringifyYaml(ordered, {
    lineWidth: 0, // never wrap long descriptions
    defaultStringType: "QUOTE_DOUBLE",
    defaultKeyType: "PLAIN",
  }).trimEnd();
}

const FRONTMATTER_KEY_ORDER = [
  "title",
  "slug",
  "description",
  "date",
  "updated",
  "status",
  "category",
  "tags",
  "cover",
  "seo",
] as const;

/** Compose a complete Markdown file from frontmatter and a body. */
export function composeDocument(data: Frontmatter, body: string): string {
  const frontmatter = serializeFrontmatter(data);
  const trimmedBody = body.replace(/^\r?\n+/, "").trimEnd();

  return `---\n${frontmatter}\n---\n\n${trimmedBody}\n`;
}
